import queryClient from "api/queryClient.ts";
import { incomingPostbackApi } from "api/TenantSlug/IncomingPostback/api.ts";
import {
    useGetIncomingPostbackQuery,
    useUpdateIncomingPostbackMutation,
} from "api/TenantSlug/IncomingPostback/hooks.ts";
import { DiagramWithStorage } from "components/CC/Diagrams/DiagramWithStorage/DiagramWithStorage.tsx";
import { CCEntityType } from "components/CC/types/entityConfig.ts";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { useCallback } from "react";
import { PermissionEnum } from "types/enums.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { getTrackingToolFromItems } from "utils/tenantHeaderItems.ts";

export const Route = createTenantFileRoute(
    "/_authenticated/$tenantSlug/_layout/tool/_layout/tracking/_layout/s2s-postback/_layout/$id/_layout/editor/"
)({
    component: TrackingToolFromEditorPage,
    staticData: {
        permissions: [PermissionEnum.ToolTrackingIncomingEditEditor],
        showInSideMenu: false,
        label: "pages.tool.tracking.from.editor.title",
    },
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.tool.tracking.from.editor.title"),
        };
    },
});

function TrackingToolFromEditorPage() {
    const { id } = Route.useParams();

    const {
        data: freshData,
        isLoading,
        isError,
    } = useGetIncomingPostbackQuery(id, { filter: { include: "scenarios" } }, { staleTime: 0 });

    const incomingPostback = freshData?.incomingPostback;
    const scenarioId = incomingPostback?.scenarios?.[0]?.id ?? null;

    const updatePostbackMutation = useUpdateIncomingPostbackMutation(id);

    const onScenarioCreated = useCallback(
        async (newScenarioId: string): Promise<void> => {
            // Validate entity still exists before linking the scenario
            const freshData = await queryClient.fetchQuery({
                queryKey: ["getIncomingPostback", id, "scenarioLink"],
                queryFn: () => incomingPostbackApi.show(id, {}),
                staleTime: 0,
            });

            const postback = freshData.incomingPostback;
            if (!postback) {
                throw new Error("Failed to link scenario: incoming postback payload is missing.");
            }

            return new Promise((resolve, reject) => {
                updatePostbackMutation.mutate(
                    { scenario_ids: { 0: newScenarioId } },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({ queryKey: ["getIncomingPostback", id] });
                            resolve();
                        },
                        onError: reject,
                    }
                );
            });
        },
        [id, updatePostbackMutation]
    );

    if (!incomingPostback) {
        return (
            <QueryState
                isLoading={isLoading}
                isError={isError || !isLoading}
                errorKey="errors.loading_incoming_postback"
            />
        );
    }

    return (
        <TenantPage headerNavigationItems={getTrackingToolFromItems} isEdit>
            <SectionContentWrapper withBackground>
                <DiagramWithStorage
                    key={id}
                    entityId={incomingPostback.id}
                    entityType={CCEntityType.TrackingIn}
                    scenarioId={scenarioId}
                    onScenarioCreated={onScenarioCreated}
                />
            </SectionContentWrapper>
        </TenantPage>
    );
}
